import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { IPackagingProductItem, IPackagingProductListItem } from 'src/types/packaging-product';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  products: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IPackagingProductListItem[];
  };
  product: IPackagingProductItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  products: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  product: null,
};

const packagingProductSlice = createSlice({
  name: 'packagingProduct',
  initialState,
  reducers: {
    removePackagingProduct: (state, action) => {
      const { productId } = action.payload;

      const index = state.products.items.findIndex((product) => product._id === productId);

      if (index > -1) {
        action.payload.index = index;
        state.products.items = state.products.items.filter(
          (product) => product._id !== productId
        );
      }
    },
    rollbackPackagingProductDeletion: (state, action) => {
      const { product, index } = action.payload;

      state.products.items.splice(index, 0, product);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaginatedPackagingProducts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPaginatedPackagingProducts.fulfilled, (state, action) => {
        state.status = 'success';
        state.products = action.payload;
      })
      .addCase(getPaginatedPackagingProducts.rejected, (state, action) => {
        state.products = initialState.products;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getPackagingProductById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPackagingProductById.fulfilled, (state, action) => {
        state.status = 'success';
        state.product = action.payload;
        state.error = initialState.error;
      })
      .addCase(getPackagingProductById.rejected, (state, action) => {
        state.product = initialState.product;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default packagingProductSlice.reducer;

export const { removePackagingProduct, rollbackPackagingProductDeletion } =
  packagingProductSlice.actions;

export const getPaginatedPackagingProducts = createAsyncThunk(
  'packagingProduct/paginated',
  async (params: {
    page: number | null;
    limit: number | null;
    generalSearch: string | null;
    status?: string | null;
    sortBy: string | null;
    sort: 'asc' | 'desc' | null;
  }) => {
    try {
      const response = await axios.get(
        `product?page=${params.page !== null ? params.page + 1 : ''}&limit=${
          params.limit ?? ''
        }&generalSearch=${params.generalSearch ?? ''}&status=${
          params.status ?? 'active'
        }&sortBy=${params.sortBy}&sort=${params.sort}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getPackagingProductById = createAsyncThunk(
  'packagingProduct/getById',
  async (params: { productId: string }) => {
    const { productId } = params;

    try {
      const response = await axios.get(`product/${productId}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

// Backend soft-disables (status -> inactive) rather than hard-deleting, so
// historical dispatch/return records that reference a product stay valid.
export const deletePackagingProduct = createAsyncThunk(
  'packagingProduct/delete',
  async (params: { product: IPackagingProductListItem; index: number }, { dispatch }) => {
    const { product, index } = params;

    try {
      const response = await axios.delete(`product/${product._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackPackagingProductDeletion({ product, index }));
      throw err;
    }
  }
);

export interface TCreatePackagingProduct {
  productName: string;
  productCode?: string | null;
}

export const createPackagingProduct = createAsyncThunk(
  'packagingProduct/create',
  async (params: { product: TCreatePackagingProduct }) => {
    const { product } = params;

    const response = await axios.post('product', product);

    return response.data.data;
  }
);

export const updatePackagingProduct = createAsyncThunk(
  'packagingProduct/update',
  async (params: { productId: string; product: TCreatePackagingProduct }) => {
    const { productId, product } = params;

    const response = await axios.put(`product/${productId}`, product);

    return response.data.data;
  }
);
